import React from 'react';
import {useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import Card from "../../../components/Card";
import DescriptionList from "../../../components/DescriptionList";
import DescriptionListItem from "../../../components/DescriptionListItem";
import DescriptionTerm from "../../../components/DescriptionTerm";
import DescriptionDetails from "../../../components/DescriptionDetails";
import {formattedDate} from "../../../services/date";
import Chip from "../../../components/Chip";
import Page from "../../../components/Page";
import {toTitleCase} from "../../../services/string";
import DotBadge from "../../../components/DotBadge";
import {UserIcon} from "@heroicons/react/solid";
import {Voucher} from "../../../types/Voucher";
import {DateTime} from "luxon";

// Helpers ----

function determineVoucherStatus(voucher: Voucher): string {
  const now = DateTime.now()

  if(voucher.valid_at > now) {
    return 'invalid'
  }

  if(voucher.expires_at < now) {
    return 'expired'
  }

  if(!voucher.has_uses_remaining) {
    return 'used'
  }

  return 'valid'
}

const statusColors: Record<string, number> = {
  invalid: DotBadge.colors.BLUE,
  expired: DotBadge.colors.GRAY,
  used: DotBadge.colors.YELLOW,
  valid: DotBadge.colors.GREEN,
  unknown: DotBadge.colors.GRAY,
}

// Component ----

function ViewVoucher() {
  const {id} = useParams()

  const {data: voucher, isLoading} = useQuery(
    ['vouchers', id],
    () => ngage.vouchers.get(Number(id))
  )

  const voucherStatus = voucher? determineVoucherStatus(voucher) : 'unknown'

  const breadcrumbs = {
    "Engagement": "/",
    "Offers": "/offers",
    [voucher?.offer?.name || "View Offer"]: `/offers/${voucher?.offer?.id}`,
    "Voucher": `/vouchers/${id}`
  }

  return (
    <Page title={`Voucher for ${voucher?.offer?.name}`} breadcrumbs={breadcrumbs} isLoading={isLoading}>
      {!!voucher &&
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          {/* Voucher Information */}
          <section className="lg:col-span-2">
            <Card
              heading="Voucher Information"
              subheading={toTitleCase(voucher.offer?.name || '')}
            >
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Status</DescriptionTerm>
                  <DescriptionDetails>
                    <DotBadge color={statusColors[voucherStatus]} text={voucherStatus} />
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Issued</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(voucher.issued_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Valid From</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(voucher.valid_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Expires End</DescriptionTerm>
                  <DescriptionDetails>
                    {voucher.expires_at? formattedDate(voucher.expires_at) : 'Never'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Uses remaining</DescriptionTerm>
                  <DescriptionDetails>
                    {voucher.uses_remaining}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Holder</DescriptionTerm>
                  <DescriptionDetails>
                    <Chip
                      name={voucher.holder?.name || 'Unknown Holder'}
                      description="Voucher holder"
                      href={voucher.holder? `/audience/${voucher.holder.id}` : ''}
                    />
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          <section className="mt-4 lg:mt-0 lg:col-span-1">
            <Card heading="QR Code">
              <div className="relative flex justify-center items-center overflow-hidden">
                <img
                  src={voucher.qr_code}
                  alt={`QR code for voucher ${voucher.code}`}
                  className="z-10"
                />
                <UserIcon className="absolute top-16 lg:top-12 left-0 right-0 h-auto text-gray-200" />
              </div>
            </Card>
          </section>
        </div>
      }
    </Page>
  );
}

export default ViewVoucher;